// Job — the work list for one job: address and directions, WHO ELSE IS ON IT,
// the checklist, photos and files, notes, the clock button for this job, the
// job's own status, and who has been here.
//
// ── The three things this screen got back on 2026-09-09 ────────────────────
// (docs/PARITY_2026-09-08.md; the server already answered all three)
//
//   §8   the CREW. GET /api/field/job/:id has attached `job.assignees`
//        specifically so this screen could show it, and this screen never
//        read it — so a crew member could not tell whether they were meeting
//        somebody at the property or were on their own. It is drawn high, by
//        the address, because it is a before-you-arrive question.
//   §6   FILES, not only photos. Both upload inputs in the app said
//        `accept="image/*"` while routes/media.js has always taken a PDF, and
//        every row was drawn as an `<img>`, so a permit added before the
//        rebuild rendered as a broken image on a phone.
//   §16  a PAGER. A photo was an `<a target="_blank">` — out of the app, one
//        photo at a time, no way back but the OS gesture and no download.
//
// Ticks, notes and photos go through actions.js and so survive no signal;
// the checklist updates on screen the moment it is tapped and is only put
// back if the server says no.
//
// Start / Pause / Mark done is the exception: it is a live request that
// refuses when there is no signal, because /api/field/sync has no status op
// and a queued one would be dropped hours later (actions.js setJobStatus).
// Which buttons appear, and to whom, is apps/shared/job/jobStatusActions.js —
// the server only lets the LEAD or the office change a status, and this
// screen is reachable by every crew member on the roster.

import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { JobCard, CheckRow, Pill, FwButton, Notice, SectionLabel, EmptyState, Skeleton, Sheet, FwField, FwTextarea, PersonRow, BigClock, initialsOf } from '../../../ui/fw';
import { Icon } from '../../../ui';
import MediaViewer from '../../shared/job/MediaViewer';
import { MEDIA_ACCEPT, PHOTO_ACCEPT, isImageFile, imagesOf, fileIcon, fileLabel, fileHref, fmtBytes } from '../../shared/job/mediaKinds';
import { describeError, serverSentence } from '../../shared/http';
import { useAuth, useSettings } from '../../../App';
import { statusActionsFor, whyStatusRefused, statusDoneLabel, validateStatusChange } from '../../shared/job/jobStatusActions';
import { statusLabel, statusTone } from '../../shared/jobFields';
import StopSheet from '../StopSheet';
import { useResolvedNow, useOfflineQueue } from '../../shell/offline';
import { usePageChrome } from '../../shell/chrome';
import { useToday, useJob, jobKey, TODAY_KEY } from '../queries';
import { clockIn, clockOut, toggleSubtask, addNote, addPhotos, setJobStatus } from '../actions';
import { photoOutcome } from '../../shared/photoBatches';
import { gpsRequired } from '../gpsGate';
import { gpsText, accuracyText, mapHref } from '../../shared/geoFix';
import { whenLabel, durationLabel, staleLabel, plural, timeOf, scheduleLabel, loggedSeconds } from '../fmt';
import JobHelp from '../../shared/JobHelp';

/**
 * How many clock entries GET /api/field/job/:id will return, at most.
 *
 * `server/routes/field.js` ends that query `ORDER BY te.start_time DESC LIMIT
 * 50`. It matters here because the "Logged" fact below is a sum of what
 * arrived, and on a long crew job the answer would silently be a sum of the
 * fifty most recent shifts presented as the whole job. A short number labelled
 * as the total is the kind of plausible-looking wrong figure CLAUDE.md calls
 * the worst outcome available, so the label says so instead.
 */
export const ENTRY_LIMIT = 50;

/**
 * WHERE a shift was started, on the shift.
 *
 * ── Why this is here ───────────────────────────────────────────────────────
 *
 * `time_entries.latitude/longitude` are written on every clock in and out
 * (apps/field/geo.js -> routes/field.js applyOp) and `GET /api/field/job/:id`
 * did not SELECT either of them, so the one screen a crew member works from
 * could not show a fix the app had just taken off the phone in their hand.
 * docs/APP_REBUILD.md §3.4 names it: "location collected and never shown".
 *
 * ── Why the radius is beside the link and not hidden behind it ─────────────
 *
 * "±8 m" and "±2 km" are the same pin on a map and completely different
 * evidence: an 8 m satellite fix says which side of the house, a 2 km wifi
 * triangulation says which town. iOS returns the second, deliberately, when
 * Precise Location is off for a site — with no other signal that anything is
 * wrong, which is what the owner's "a mile away from location" report of
 * 2026-09-10 actually was. Without the radius the coordinates read as
 * confident to four decimal places either way.
 *
 * ── Why an entry with no fix renders NOTHING ───────────────────────────────
 *
 * Not an em dash, not a greyed link, not "no location". A dead link is the
 * failure apps/shared/jobLink.js `jobRecordHref` returns '' to avoid — a
 * control that looks tappable, is tapped, and does nothing. `mapHref` already
 * makes that decision for a fix, so this asks it rather than re-deciding, and
 * a row with nothing to say costs a crew member no line of a 320px screen.
 *
 * Every reading here goes through apps/shared/geoFix.js — the app's one reader
 * of a fix, which already handles the two spellings the server answers with
 * and refuses a radius of zero or less. A second copy would be a second
 * behaviour to keep working.
 */
export function EntryFix({ entry }) {
  const href = mapHref(entry);
  if (!href) return null;
  const radius = accuracyText(entry);
  return (
    <span className="fd-entry__fix">
      <a className="fd-entry__map" href={href} target="_blank" rel="noopener noreferrer">
        <Icon name="mapPin" />
        <span>{gpsText(entry)}</span>
      </a>
      {radius ? <span className="fd-entry__acc">{radius}</span> : null}
    </span>
  );
}

/**
 * The facts a crew member opens a job to find, in the order they need them.
 *
 * ── What this is fixing, measured against the endpoint ──────────────────────
 *
 * `server/routes/field.js shapeJob()` returns client_name, property_name,
 * address, scheduled_date, scheduled_end_date, time_label, estimated_hours,
 * status, lead_name and the roster on EVERY job. Before 2026-09-11 this screen
 * drew the client and the property in exactly one place — as the `meta` line
 * of the directions card — so a job with no address on file (no property row,
 * or a property with no street) named neither, anywhere. `scheduled_date`,
 * `scheduled_end_date` and `estimated_hours` were drawn nowhere at all, on any
 * job. That is "jobs showing nothing": the answer had the facts in it and the
 * screen did not read them.
 *
 * A fact is only included when it HAS a value. An em dash in a labelled slot
 * costs a line of a phone screen and tells a person nothing — the one
 * exception is "Scheduled", because a job sitting on your phone with no date
 * on it is itself information a crew member acts on.
 *
 * Pure and exported so it can be tested without a browser; the screen spreads
 * the result into rows.
 */
export function jobDetailFacts(job, entries = []) {
  if (!job) return [];
  const out = [];
  if (job.client_name) out.push({ key: 'client', label: 'Client', value: job.client_name });
  if (job.property_name) out.push({ key: 'property', label: 'Property', value: job.property_name });
  out.push({ key: 'when', label: 'Scheduled', value: scheduleLabel(job) || 'Not scheduled' });
  if (job.estimated_hours) out.push({ key: 'estimate', label: 'Estimate', value: plural(job.estimated_hours, 'hr') });
  const logged = loggedSeconds(entries);
  if (logged > 0) {
    out.push({
      key: 'logged',
      label: entries.length >= ENTRY_LIMIT ? `Logged (last ${ENTRY_LIMIT})` : 'Logged',
      value: durationLabel(logged),
    });
  }
  // Last, and always: it is what the office asks for on the phone.
  out.push({ key: 'number', label: 'Job number', value: `#${job.id}` });
  return out;
}

export default function Job() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const off = useOfflineQueue();
  const [helpOpen, setHelpOpen] = useState(false);
  const { user } = useAuth();
  const today = useToday();
  const now = useResolvedNow(today);
  const job = useJob(id);
  const fileRef = useRef(null);
  const docRef = useRef(null);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [queuedNotes, setQueuedNotes] = useState([]);
  const [statusAsk, setStatusAsk] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [statusDone, setStatusDone] = useState(null);
  // Same three answers as Today: a refusal when the company requires a
  // location, an ask when it does not, a warning when the fix is too wide to
  // name a property. Decided once, in actions.js, so the two screens cannot
  // drift the way the stop reason did.
  const [gps, setGps] = useState(null);
  const { settings } = useSettings();
  const requireGps = gpsRequired(settings);

  const data = job.data;
  const j = data && data.job;
  const openPrereqs = ((data && data.waiting_on) || []).filter((p) => p.status !== 'completed');
  const onThisJob = !!(now && String(now.job_id) === String(id));

  // The header names the JOB, not the section — you already know you tapped
  // a job. Falls back to the destination label while it loads.
  usePageChrome(j ? j.title : 'Job', 'Today');
  const pendingPhotos = off.photos.pendingFor(id).length;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: jobKey(id) });
    qc.invalidateQueries({ queryKey: TODAY_KEY });
  };

  const run = async (label, fn, { refresh = true } = {}) => {
    setBusy(label);
    setError(null);
    try {
      const out = await fn();
      if (refresh && out && out.sent) invalidate();
      return out;
    } catch (e) {
      setError(describeError(e, 'That did not go through'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const onToggle = (subtask, next) => {
    qc.setQueryData(jobKey(id), (prev) => (prev ? {
      ...prev,
      subtasks: prev.subtasks.map((s) => (s.id === subtask.id ? { ...s, completed: next ? 1 : 0 } : s)),
    } : prev));
    run(`st:${subtask.id}`, async () => {
      try {
        return await toggleSubtask(subtask.id, next);
      } catch (e) {
        qc.setQueryData(jobKey(id), (prev) => (prev ? {
          ...prev,
          subtasks: prev.subtasks.map((s) => (s.id === subtask.id ? { ...s, completed: next ? 0 : 1 } : s)),
        } : prev));
        throw e;
      }
    });
  };

  // One handler for both inputs — the camera and the file picker differ only
  // in what they will OFFER (mediaKinds.js), never in what happens after. The
  // FileList is COPIED before the input is cleared: `input.files` is live-bound
  // to the element, so clearing it first would hand addPhotos an empty list.
  // Clearing it at all is what lets the same file be picked twice in a row, and
  // `busy` is per-input so pressing one does not put a spinner on the other.
  //
  // What is said afterwards is the point. addPhotos used to throw on the first
  // refusal and abandon every photo after it, so picking thirty and having the
  // third refused showed one red sentence about one photo and silently dropped
  // twenty-seven. It reports now — `{ sent, queued, refused }` — and
  // photoOutcome turns that into a sentence with both counts in it. A partial
  // upload that reads as a success is how somebody drives away from a property
  // they have to go back to.
  const onFiles = (label) => async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length) return;
    const out = await run(label, async () => {
      const r = await addPhotos(Number(id), files);
      return { sent: r.sent > 0, photos: r };
    });
    // `run` answers null when addPhotos threw, and the Notice already carries
    // the server's own sentence for that.
    if (out && out.photos) {
      const sentence = photoOutcome(out.photos);
      if (sentence) setError(sentence);
    }
  };

  const saveNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    const out = await run('note', () => addNote(Number(id), text));
    if (out) {
      if (!out.sent) setQueuedNotes((q) => [{ key: out.queued.key, comment: text, created_at: out.queued.at }, ...q]);
      setNoteText('');
      setNoteOpen(false);
    }
  };

  // Stopping asks why, exactly as Today does — same sheet, one definition.
  // This button sent `note: ''` until 2026-09-04, a day after the same defect
  // was fixed on Today (#545), so a stop made from the job screen still told
  // dispatch nothing: server/routes/field.js only notifies when a reason came
  // with the op. Clocking IN is unchanged — there is nothing to explain.
  //
  // Clocking IN goes through its own handler rather than `run`, for the same
  // reason it does on Today: a location refusal is an instruction with a title
  // and a fix, not a "that did not go through".
  const onClock = async () => {
    if (onThisJob) { setStopOpen(true); return; }
    if (!j) return;
    // On ANOTHER job: clocking in here stops that one, so ask why first —
    // classic would not start a second job until the first was stopped with
    // a reason. Until 2026-10-09 the server closed it as "Closed by next
    // clock-in" and dispatch heard nothing.
    if (now && now.job_id != null) { setError(null); setGps(null); setSwitchOpen(true); return; }
    setBusy('clock');
    setError(null);
    setGps(null);
    try {
      const out = await clockIn(j, { gpsRequired: requireGps });
      if (out.sent) invalidate();
      if (out.gps && out.gps.message) setGps(out.gps);
    } catch (e) {
      if (e && e.gps) setGps(e.gps);
      else setError(describeError(e, 'That did not go through'));
    } finally {
      setBusy(null);
    }
  };

  // The switch: ONE clock-in that carries the reason for the job it closes
  // (actions.js clockIn `closeNote`). Resolves the sentence for the sheet, as
  // onClockOut does — a location refusal included, since nothing was sent and
  // the other job is still running, which the sentence has to be read to know.
  const onSwitch = async (reason) => {
    setBusy('clock');
    setError(null);
    setGps(null);
    try {
      const out = await clockIn(j, { gpsRequired: requireGps, closeNote: reason });
      if (out.sent) invalidate();
      if (out.gps && out.gps.message) setGps(out.gps);
      return null;
    } catch (e) {
      if (e && e.gps) return `${e.gps.message} You are still clocked in to ${(now && now.job_title) || 'the other job'}.`;
      return describeError(e, 'That did not go through');
    } finally {
      setBusy(null);
    }
  };

  // Resolves the sentence for the sheet rather than setting `error`: the sheet
  // keeps the typed reason through a refusal, and this screen's alert bar is
  // at the top of the page, behind it.
  const onClockOut = async (note) => {
    setBusy('clock');
    setError(null);
    try {
      const out = await clockOut(now, { note });
      if (out && out.sent) invalidate();
      return null;
    } catch (e) {
      return describeError(e, 'That did not go through');
    } finally {
      setBusy(null);
    }
  };

  // A status change is a live request. Start goes straight through; Pause and
  // Mark done open a sheet first, because the office reads what is typed there
  // and a pause with no reason is the thing the classic screen refuses.
  const askStatus = (action) => {
    setError(null);
    setStatusDone(null);
    if (action.note === 'none') { sendStatus(action, ''); return; }
    setStatusNote('');
    setStatusAsk(action);
  };

  const sendStatus = async (action, note) => {
    setBusy(`status:${action.key}`);
    setError(null);
    try {
      await setJobStatus(j, action, note);
      setStatusAsk(null);
      setStatusNote('');
      setStatusDone(statusDoneLabel(action));
      invalidate();
    } catch (e) {
      // The server's own sentence when it sent one — "Cannot complete:
      // prerequisite jobs not done: …", "Access denied", or the offline
      // refusal this action raises itself.
      //
      // The case that needs serverSentence rather than describeError is the
      // 403: describeError flattens every one of them to "You do not have
      // permission to do that", which is true and tells a crew member nothing
      // about which door was shut. (A 400 carrying a body sentence survives
      // either function, because HttpError already takes its message from the
      // body — checked by sabotage on 2026-09-04, where swapping this line
      // took only the 403 case red.)
      setError(serverSentence(e, 'That did not go through'));
      setStatusAsk(null);
    } finally {
      setBusy(null);
    }
  };

  let body;
  if (job.isPending && !data) {
    body = <Skeleton lines={6} />;
  } else if (job.error && !data) {
    body = (
      <Notice tone="stop" title="Could not load this job">
        {describeError(job.error)}{' '}
        <FwButton variant="quiet" onClick={() => job.refetch()}>Try again</FwButton>
      </Notice>
    );
  } else if (j) {
    const subtasks = data.subtasks || [];
    const done = subtasks.filter((s) => s.completed).length;
    const photos = data.photos || [];
    const images = imagesOf(photos);
    const files = photos.filter((p) => !isImageFile(p));
    const viewIndex = images.findIndex((p) => String(p.id) === String(viewing));
    const crew = j.assignees || [];
    const notes = data.notes || [];
    const entries = data.entries || [];
    const statusActions = statusActionsFor(j, user);
    const refusedWhy = whyStatusRefused(user, j);
    // The shared map, not a ladder. This one said archived -> neutral while
    // Office said stop, so the same job wore two different colours.
    const tone = statusTone(j.status);
    const facts = jobDetailFacts(j, entries);
    const left = subtasks.length - done;
    body = (
      <>
        {data.__stale ? <div className="fd-stale">No signal — showing this job {staleLabel(data.__stale)}</div> : null}
        {error ? <Notice tone="stop" onDismiss={() => setError(null)}>{error}</Notice> : null}
        {gps ? <Notice tone={gps.tone} title={gps.title} onDismiss={() => setGps(null)}>{gps.message}</Notice> : null}
        {j.blocked ? <Notice tone="stop" title="Blocked">{j.blocked_reason || 'This job is blocked.'}</Notice> : null}
        {j.no_client ? <Notice tone="warn">This job has no client or property. Tell the office so it can be billed.</Notice> : null}

        {/* The clock for THIS job, ticking, with seconds — the owner:
            "Timer running should show seconds, so we can see timer is actually
            running." Today has carried a BigClock since the rebuild and the
            job screen had nothing: a crew member standing on the job, on the
            screen they work from, could see a Clock out button and no evidence
            the clock was running at all. Same component as Today's LiveCard,
            so there is one elapsed calculation in the app, not two. */}
        {onThisJob && now ? (
          <div className="fd-running" data-section="running">
            <div className="fd-running__pills">
              <Pill tone="now">On the clock</Pill>
              {now.queued ? <Pill tone="sched">Queued</Pill> : null}
            </div>
            <BigClock startedAt={now.started_at} size="lg" />
            {now.started_at ? <div className="fd-running__since">Started {timeOf(now.started_at)}</div> : null}
          </div>
        ) : null}

        {j.address ? (
          j.directions_url ? (
            <a className="fd-directions" href={j.directions_url} target="_blank" rel="noopener noreferrer" aria-label={`Directions to ${j.address}`}>
              <JobCard time={<Icon name="mapPin" />} title={j.address} meta={[j.client_name, j.property_name].filter(Boolean).join(' · ') || 'Directions'} tone="sched" />
            </a>
          ) : (
            <JobCard time={<Icon name="mapPin" />} title={j.address} meta={[j.client_name, j.property_name].filter(Boolean).join(' · ') || null} />
          )
        ) : null}

        {/* Who it is for, where, when, and how long it is meant to take — see
            jobDetailFacts above for what the server was already sending and
            this screen was not reading. It is a section of its own rather than
            a meta line on the address card, because the card is only drawn
            when there IS an address, and the job with no address is exactly
            the one a crew member has the most questions about. */}
        <div className="fd-section" data-section="facts">
          <SectionLabel>This job</SectionLabel>
          <dl className="fd-facts">
            {facts.map((f) => (
              <div className="fd-fact" key={f.key}>
                <dt className="fd-fact__label">{f.label}</dt>
                <dd className="fd-fact__value">{f.value}</dd>
              </div>
            ))}
          </dl>
          {/* Only when there is somebody to ask about. A job with no client and
              no property already has the notice at the top of this screen, and
              two sentences saying the same thing is how a crew learns to skip
              both. */}
          {!j.address && !j.no_client ? (
            <div className="fd-hint">No address on file for this job. Ask the office before you set off.</div>
          ) : null}
        </div>

        {j.description ? (
          <div className="fd-section" data-section="description">
            <SectionLabel>What to do</SectionLabel>
            <div className="fd-item"><div className="fd-item__text">{j.description}</div></div>
          </div>
        ) : null}

        {/* Who else is on this job (§8). Named people, not a count: "2 crew"
            does not tell somebody who to look for in a driveway. The lead is
            marked, because the lead is who may change the status and is who
            dispatch will have called. A roster of one that is YOU says so in
            a sentence — an empty section reads as "we could not ask". */}
        <div className="fd-section" data-section="crew">
          <SectionLabel>Crew{crew.length > 1 ? ` — ${crew.length}` : ''}</SectionLabel>
          {/* UNASSIGNED, as a word in a pill, when there is neither a lead nor
              a roster. The owner: "Need to see who's assigned/unassigned".
              This screen is reachable for a job nobody is on — Today lists
              those under "Up for grabs" and the card opens here — and the
              sentence below it ("Nobody is on this job yet") is a line of
              muted body text three sections down a phone screen. The state a
              crew member is deciding on deserves the same treatment as every
              other state on this screen. */}
          {crew.length === 0 && !j.lead_name ? (
            <div className="fd-statusrow"><Pill tone="warn">Unassigned</Pill></div>
          ) : null}
          {crew.length === 0 ? (
            <div className="fd-hint">
              {j.lead_name
                ? `${j.lead_name} is the lead on this job. Nobody else is on it.`
                : 'Nobody is on this job yet.'}
            </div>
          ) : (
            <div className="fd-crew">
              {crew.map((a) => (
                <PersonRow
                  key={a.id}
                  initials={initialsOf(a.name)}
                  name={a.name}
                  meta={user && String(a.id) === String(user.id) ? 'You' : null}
                  tone={a.is_lead ? 'ok' : 'neutral'}
                  trailing={a.is_lead ? <Pill tone="ok">Lead</Pill> : null}
                />
              ))}
              {crew.length === 1 ? (
                <div className="fd-hint">
                  {user && String(crew[0].id) === String(user.id)
                    ? 'You are on your own on this job.'
                    : 'Nobody else is on this job.'}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="fd-section" data-section="status">
          <SectionLabel>Job status</SectionLabel>
          {/* The status itself is ALWAYS shown. Only the buttons are gated, so
              a crew member who may not change it still knows what it is. */}
          <div className="fd-statusrow">
            <Pill tone={tone}>{statusLabel(j.status)}</Pill>
            {statusDone ? <span className="fd-status-done">{statusDone}</span> : null}
          </div>
          {refusedWhy ? <div className="fd-hint">{refusedWhy}</div> : null}
          {/* Jobs this one waits on. Complete is refused until they are done,
              so say which, before the refusal does. */}
          {openPrereqs.length ? (
            <Notice tone="warn" data-waiting-on>
              Finish first: {openPrereqs.map((p) => `${p.title} (${statusLabel(p.status)}${p.lead_name ? ` · ${p.lead_name}` : ''})`).join('; ')}.
            </Notice>
          ) : null}
          {statusActions.length > 0 ? (
            <>
              {!off.online ? (
                <Notice tone="warn">
                  Changing a job&apos;s status needs a connection. Clocking in and out, ticks, notes and photos are saved on this phone as usual.
                </Notice>
              ) : null}
              <div className="fd-statusbtns">
                {statusActions.map((a) => (
                  <FwButton
                    key={a.key}
                    variant={a.variant}
                    size="lg"
                    icon={a.icon}
                    onClick={() => askStatus(a)}
                    loading={busy === `status:${a.key}`}
                    disabled={!off.online || (busy !== null && busy !== `status:${a.key}`)}
                    aria-label={`${a.label} — ${j.title}`}
                    data-status-action={a.key}
                  >
                    {a.label}
                  </FwButton>
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className="fd-section" data-section="worklist">
          <SectionLabel>Work list{subtasks.length ? ` — ${done} of ${subtasks.length}` : ''}</SectionLabel>
          {/* A count in a muted 12px heading is not a signal — "1 of 2" reads
              the same as "2 of 2" at arm's length in sunlight. The state gets
              a pill of its own, in a WORD and a tone: unfinished work is the
              thing the day-end review asks about, and a crew member has to be
              able to see it without counting rows. */}
          {subtasks.length > 0 ? (
            <div className="fd-statusrow">
              {left > 0
                ? <Pill tone="warn">{plural(left, 'item')} left</Pill>
                : <Pill tone="ok">All done</Pill>}
            </div>
          ) : null}
          {subtasks.length === 0 ? (
            <EmptyState icon="list" title="No work list on this job" />
          ) : subtasks.map((s) => (
            <CheckRow
              key={s.id}
              checked={!!s.completed}
              label={s.title}
              meta={s.description || null}
              onToggle={(next) => onToggle(s, next)}
            />
          ))}
        </div>

        <div className="fd-section" data-section="media">
          <SectionLabel>Photos &amp; files{pendingPhotos ? ` — ${plural(pendingPhotos, 'photo')} waiting to send` : ''}</SectionLabel>
          {/* Two controls, because they are two different actions on a phone.
              The camera one keeps `capture="environment"` and `image/*` — that
              is what makes the rear camera open on the first tap, and it is
              the thing crews use fifty times a day. The second is the picker,
              and it is the one that was missing: a permit, a signed change
              order, a scanned receipt (§6). */}
          <div className="fd-addrow">
            <FwButton variant="ghost" icon="camera" full onClick={() => fileRef.current && fileRef.current.click()} loading={busy === 'photo'}>
              Take a photo
            </FwButton>
            <FwButton variant="ghost" icon="document" full onClick={() => docRef.current && docRef.current.click()} loading={busy === 'file'}>
              Add a file
            </FwButton>
          </div>
          <input
            ref={fileRef}
            className="fd-file"
            type="file"
            accept={PHOTO_ACCEPT}
            capture="environment"
            multiple
            aria-label="Take a photo"
            onChange={onFiles('photo')}
          />
          <input
            ref={docRef}
            className="fd-file"
            type="file"
            accept={MEDIA_ACCEPT}
            multiple
            aria-label="Add a file"
            onChange={onFiles('file')}
          />
          {images.length > 0 ? (
            <div className="fd-photos">
              {images.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  className="fd-photo"
                  onClick={() => setViewing(p.id)}
                  aria-label={`Open ${fileLabel(p)}`}
                >
                  <img src={fileHref(p)} alt={p.caption || `Photo by ${p.user_name || 'crew'}`} loading="lazy" data-photo-index={i} />
                </button>
              ))}
            </div>
          ) : null}
          {/* A document is a row, not a tile: there is no thumbnail to look
              at, and the NAME is the whole of what tells a crew member which
              permit this is. Rendering one as an <img> is what put a broken
              image on this screen. */}
          {files.length > 0 ? (
            <div className="fd-list">
              {files.map((p) => (
                <a
                  key={p.id}
                  className="fd-doc"
                  href={fileHref(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${fileLabel(p)}`}
                >
                  <span className="fd-doc__glyph" aria-hidden="true"><Icon name={fileIcon(p)} /></span>
                  <span className="fd-doc__text">
                    <span className="fd-doc__name">{fileLabel(p)}</span>
                    <span className="fd-doc__meta">
                      {[p.caption, fmtBytes(p.file_size), p.user_name].filter((x) => x && x !== '—').join(' · ')}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          ) : null}
        </div>

        <div className="fd-section">
          <SectionLabel>Notes</SectionLabel>
          <FwButton variant="ghost" icon="plus" full onClick={() => setNoteOpen(true)}>Add a note</FwButton>
          <div className="fd-list">
            {queuedNotes.map((n) => (
              <div key={n.key} className="fd-item">
                <div className="fd-item__text">{n.comment}</div>
                <div className="fd-item__meta"><Pill tone="sched">Queued</Pill><span>{whenLabel(n.created_at)}</span></div>
              </div>
            ))}
            {notes.map((n) => (
              <div key={n.id} className="fd-item">
                <div className="fd-item__text">{n.comment}</div>
                <div className="fd-item__meta"><span>{n.user_name || 'Crew'}</span><span>{whenLabel(n.created_at)}</span></div>
              </div>
            ))}
            {notes.length === 0 && queuedNotes.length === 0 ? <div className="fd-hint">No notes yet.</div> : null}
          </div>
        </div>

        {entries.length > 0 ? (
          <div className="fd-section">
            <SectionLabel>Recent time</SectionLabel>
            <div className="fd-list">
              {entries.slice(0, 10).map((e) => (
                <div key={e.id} className="fd-entry">
                  <span className="fd-entry__who"><span className="fd-entry__title">{e.user_name || 'Crew'}</span> · {whenLabel(e.start_time)}</span>
                  <span className="fw-num">{e.end_time ? durationLabel(e.duration) : 'On the clock'}</span>
                  <EntryFix entry={e} />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Job help sits here — at the END of the job, after the work list,
            photos and notes — and NOT in the tab bar. REBUILD_SPEC §2 fixes
            Field at three tabs, and this is a lookup somebody does while
            standing in front of the thing, so the job screen is where it
            belongs. Above the sticky clock control, never between a crew
            member and Clock in. */}
        <div className="fd-section">
          <FwButton variant="ghost" icon="search" full onClick={() => setHelpOpen(true)}>
            Job help — look something up
          </FwButton>
        </div>

        <div className="fd-sticky">
          <FwButton
            variant={onThisJob ? 'stop' : 'go'}
            size="lg"
            full
            icon={onThisJob ? 'stop' : 'play'}
            onClick={onClock}
            loading={busy === 'clock'}
          >
            {onThisJob ? 'Clock out' : 'Clock in'}
          </FwButton>
        </div>

        <Sheet
          open={noteOpen}
          title="Add a note"
          onClose={() => setNoteOpen(false)}
          footer={<FwButton size="lg" full onClick={saveNote} loading={busy === 'note'} disabled={!noteText.trim()}>Save note</FwButton>}
        >
          <FwField label="Note" htmlFor="fd-note">
            <FwTextarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={5} placeholder="Gate was locked, used side path" />
          </FwField>
        </Sheet>

        {viewIndex >= 0 ? (
          <MediaViewer
            items={images}
            index={viewIndex}
            onIndex={(i) => setViewing(images[i] && images[i].id)}
            onClose={() => setViewing(null)}
          />
        ) : null}

        <StopSheet
          open={stopOpen}
          busy={busy === 'clock'}
          onClose={() => setStopOpen(false)}
          onStop={onClockOut}
        />
        <StopSheet
          open={switchOpen}
          busy={busy === 'clock'}
          onClose={() => setSwitchOpen(false)}
          onStop={onSwitch}
          title="Why are you stopping the other job?"
          intro={`You are clocked in to ${(now && now.job_title) || 'another job'}. Clocking in here stops it, and dispatch is told why.`}
          confirmLabel="Clock in here"
          confirmAria={`Stop ${(now && now.job_title) || 'the other job'} and clock in to ${j ? j.title : 'this job'}`}
        />

        {/* `online` is passed, not read inside: every link in there leaves the
            app, and a search button on a phone with no bars opens a tab onto a
            connection error — which reads as the app being broken. */}
        <JobHelp open={helpOpen} onClose={() => setHelpOpen(false)} online={off.online} />

        {statusAsk ? (
          <Sheet
            open
            title={statusAsk.title}
            onClose={() => setStatusAsk(null)}
            footer={(
              <>
                <FwButton variant="quiet" onClick={() => setStatusAsk(null)} disabled={busy !== null}>Cancel</FwButton>
                <FwButton
                  variant={statusAsk.variant === 'ghost' ? 'stop' : 'go'}
                  onClick={() => sendStatus(statusAsk, statusNote)}
                  loading={busy === `status:${statusAsk.key}`}
                  disabled={!!validateStatusChange(statusAsk, statusNote)}
                  aria-label={`Confirm ${statusAsk.label}`}
                >
                  {statusAsk.label}
                </FwButton>
              </>
            )}
          >
            <FwField
              label={statusAsk.note === 'required' ? 'Reason' : 'Note'}
              hint={statusAsk.hint}
              htmlFor="fd-status-note"
            >
              <FwTextarea
                id="fd-status-note"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={4}
                placeholder={statusAsk.placeholder}
                maxLength={2000}
              />
            </FwField>
          </Sheet>
        ) : null}
      </>
    );
  } else {
    body = <EmptyState icon="briefcase" title="Job not found" action={<FwButton variant="ghost" onClick={() => navigate('/today')}>Back to today</FwButton>} />;
  }

  return body;
}
